import React, { useState } from "react";
import { triggerSyonSubdocsWm } from "../../APIs/WealthManagementAPI";

interface ResendDocumentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    wmFirmClientId: string;
}

const ResendDocumentsModal: React.FC<ResendDocumentsModalProps> = ({ isOpen, onClose, wmFirmClientId }) => {
    const [currentEmail, setCurrentEmail] = useState("");
    const [emails, setEmails] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState<{ type: "success" | "error" | null; message: string }>({ type: null, message: "" });
    const [emailError, setEmailError] = useState("");

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddEmail = (e?: React.FormEvent) => {
        e?.preventDefault();
        
        if (!currentEmail.trim()) {
            setEmailError("Please enter an email address");
            return;
        }

        if (!validateEmail(currentEmail)) {
            setEmailError("Please enter a valid email address");
            return;
        }

        if (emails.includes(currentEmail)) {
            setEmailError("This email has already been added");
            return;
        }

        setEmails([...emails, currentEmail]);
        setCurrentEmail("");
        setEmailError("");
    };

    const handleRemoveEmail = (emailToRemove: string) => {
        setEmails(emails.filter(email => email !== emailToRemove));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (emails.length === 0 && currentEmail.trim()) {
            handleAddEmail();
            return;
        }

        setIsLoading(true);
        setStatus({ type: null, message: "" });

        try {
            const emailString = emails.length > 0 ? emails.join(",") : null;
            // @ts-ignore - API accepts string | null for optional email
            const resp = await triggerSyonSubdocsWm(wmFirmClientId, emailString);
            if (resp.data.msg === "Joint") setStatus({ type: "success", message: "Queued for submission. The Aqua team and will handle the sending of the documents through an alternative workflow." })
            else setStatus({ type: "success", message: "Documents sent successfully!" });
            setTimeout(() => {
                onClose();
                setEmails([]);
                setCurrentEmail("");
                setStatus({ type: null, message: "" });
            }, 2000);
        } catch (error) {
            setStatus({ type: "error", message: "Failed to send documents. Documents can be sent at most once every three hours." });
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-gray-600 tw-bg-opacity-50 tw-overflow-y-auto tw-h-full tw-w-full tw-z-50">
            <div className="tw-relative tw-top-20 tw-mx-auto tw-p-5 tw-border tw-w-96 tw-shadow-lg tw-rounded-md tw-bg-white">
                <div className="tw-mt-3">
                    <h3 className="tw-text-lg tw-leading-6 tw-font-medium tw-text-gray-900 tw-mb-4">
                        Resend Documents
                    </h3>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="tw-mb-4">
                            <label htmlFor="email" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                <span className="tw-flex tw-items-center tw-gap-1">
                                    Email Addresses (optional) - these addresses will be notified upon client's receipt of docs and signature.
                                </span>
                            </label>
                            
                            <div className="tw-flex tw-gap-2">
                                <input
                                    type="email"
                                    id="email"
                                    value={currentEmail}
                                    onChange={(e) => {
                                        setCurrentEmail(e.target.value);
                                        setEmailError("");
                                    }}
                                    onKeyDown={handleKeyDown}
                                    className="tw-flex-1 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-indigo-500 focus:tw-border-indigo-500"
                                    placeholder="Enter email address"
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddEmail}
                                    className="tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-rounded-md hover:tw-bg-gray-300"
                                    disabled={isLoading}
                                >
                                    Add
                                </button>
                            </div>
                            
                            {emailError && (
                                <div className="tw-mt-1 tw-text-sm tw-text-red-600">
                                    {emailError}
                                </div>
                            )}

                            {emails.length > 0 && (
                                <div className="tw-mt-3 tw-space-y-1">
                                    <div className="tw-text-xs tw-text-gray-600 tw-mb-1">
                                        {emails.length} email{emails.length !== 1 ? 's' : ''} added:
                                    </div>
                                    <div className="tw-max-h-32 tw-overflow-y-auto">
                                        {emails.map((email, index) => (
                                            <div key={index} className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-px-2 tw-py-1 tw-rounded tw-text-sm">
                                                <span className="tw-text-gray-700">{email}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveEmail(email)}
                                                    className="tw-text-red-500 hover:tw-text-red-700 tw-ml-2"
                                                    disabled={isLoading}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {status.type && (
                            <div className={`tw-mb-4 tw-p-2 tw-rounded ${status.type === "success" ? "tw-bg-green-100 tw-text-green-700" : "tw-bg-red-100 tw-text-red-700"}`}>
                                {status.message}
                            </div>
                        )}

                        <div className="tw-flex tw-justify-end tw-gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="tw-px-4 tw-py-2 tw-bg-gray-300 tw-text-black tw-rounded-md hover:tw-bg-gray-400"
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="tw-px-4 tw-py-2 tw-bg-indigo-600 tw-text-white tw-rounded-md hover:tw-bg-indigo-700 disabled:tw-opacity-50"
                                disabled={isLoading}
                            >
                                {isLoading ? "Sending..." : "Send"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResendDocumentsModal;